import { CheckIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import * as Select from '@radix-ui/react-select';
import React from 'react';

import { ColumnConfig, ColumnKey, ExtendedDatevBookingData } from './constants';

interface BookingCellProps {
  entry: ExtendedDatevBookingData;
  field: ColumnKey;
  isEditing: boolean;
  columnConfig: ColumnConfig;
  onChange: (field: ColumnKey, value: string | number | null) => void;
  formatCurrency: (amount: number) => string;
}

const BookingCell: React.FC<BookingCellProps> = ({
  entry,
  field,
  isEditing,
  columnConfig,
  onChange,
  formatCurrency,
}) => {
  const value = entry[field];

  if (!isEditing) {
    let displayValue: string;
    if (field === 'amount') {
      displayValue = formatCurrency(value as number);
    } else if (value === null) {
      displayValue = '-';
    } else {
      displayValue = String(value);
    }

    return (
      <td className='px-3 py-2 text-sm border-r border-gray-200 whitespace-nowrap'>
        {displayValue}
      </td>
    );
  }

  if (columnConfig.readOnly || columnConfig.type === 'readonly') {
    return (
      <td className='px-3 py-2 border-r border-gray-200 whitespace-nowrap'>
        <span className='text-xs text-gray-500'>{String(value)}</span>
      </td>
    );
  }

  if (columnConfig.type === 'select' && columnConfig.selectOptions) {
    return (
      <td className='px-3 py-2 border-r border-gray-200 whitespace-nowrap'>
        <Select.Root
          value={String(value)}
          onValueChange={val => onChange(field, val)}
        >
          <Select.Trigger className='w-full min-w-[80px] text-xs px-2 py-1 border border-gray-300 rounded bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 flex items-center justify-between'>
            <Select.Value />
            <Select.Icon>
              <ChevronDownIcon className='w-3 h-3' />
            </Select.Icon>
          </Select.Trigger>
          <Select.Portal>
            <Select.Content
              className='overflow-hidden bg-white rounded-md shadow-lg border border-gray-200 z-50'
              position='popper'
              sideOffset={5}
            >
              <Select.Viewport className='p-1'>
                {columnConfig.selectOptions.map(option => (
                  <Select.Item
                    key={option.value}
                    value={option.value}
                    className='text-xs px-2 py-1.5 rounded cursor-pointer outline-none hover:bg-blue-100 focus:bg-blue-100 flex items-center justify-between'
                  >
                    <Select.ItemText>{option.label}</Select.ItemText>
                    <Select.ItemIndicator>
                      <CheckIcon className='w-3 h-3' />
                    </Select.ItemIndicator>
                  </Select.Item>
                ))}
              </Select.Viewport>
            </Select.Content>
          </Select.Portal>
        </Select.Root>
      </td>
    );
  }

  if (columnConfig.type === 'currency' || columnConfig.type === 'number') {
    const step =
      columnConfig.step ?? (columnConfig.type === 'currency' ? '0.01' : '1');
    return (
      <td className='px-3 py-2 border-r border-gray-200 whitespace-nowrap'>
        <input
          type='number'
          step={step}
          value={value as number}
          onChange={e => {
            const parsed =
              columnConfig.type === 'currency'
                ? parseFloat(e.target.value) || 0
                : parseInt(e.target.value, 10) || 0;
            onChange(field, parsed);
          }}
          className='w-full min-w-[100px] text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500'
        />
      </td>
    );
  }

  return (
    <td className='px-3 py-2 border-r border-gray-200 whitespace-nowrap'>
      <input
        type='text'
        value={value === null ? '' : String(value)}
        onChange={e => {
          const nextValue =
            e.target.value === '' && columnConfig.nullable
              ? null
              : e.target.value;
          onChange(field, nextValue);
        }}
        className='w-full min-w-[100px] text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500'
      />
    </td>
  );
};

export default BookingCell;
